const HTTP_PROTOCOLS = new Set(["http:", "https:"]);
const IGNORED_PATH_SEGMENTS = new Set([
  "edit", "view", "client", "archives", "browse",
  "document", "documents", "file", "files", "d",
]);

export const PROJECT_SOURCE_TYPES = Object.freeze({
  SLACK: "slack",
  GOOGLE: "google",
  ATLASSIAN: "atlassian",
  ASANA: "asana",
  OTHER: "other",
});

const HOST_RULES = [
  [PROJECT_SOURCE_TYPES.SLACK, (host) => host === "slack.com" || host.endsWith(".slack.com")],
  [PROJECT_SOURCE_TYPES.GOOGLE, (host) => (
    host === "docs.google.com"
    || host.endsWith(".docs.google.com")
    || host === "drive.google.com"
    || host.endsWith(".drive.google.com")
  )],
  [PROJECT_SOURCE_TYPES.ATLASSIAN, (host) => (
    host === "atlassian.com" || host.endsWith(".atlassian.com") || host.endsWith(".atlassian.net")
  )],
  [PROJECT_SOURCE_TYPES.ASANA, (host) => host === "asana.com" || host.endsWith(".asana.com")],
];

function hostMatches(hostname, domain) {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

export function parseProjectSourceUrl(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  let parsed;
  try {
    parsed = new URL(value.trim());
  } catch {
    return null;
  }
  if (!HTTP_PROTOCOLS.has(parsed.protocol) || !parsed.hostname) return null;
  return parsed;
}

export function detectProjectSourceType(value) {
  const parsed = value instanceof URL ? value : parseProjectSourceUrl(value);
  if (!parsed) return null;
  const host = parsed.hostname.toLowerCase();
  return HOST_RULES.find(([, test]) => test(host))?.[0] || PROJECT_SOURCE_TYPES.OTHER;
}

function decodeSegment(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function cleanLabel(value) {
  return typeof value === "string"
    ? value.replace(/\s+/g, " ").trim().slice(0, 160)
    : "";
}

/**
 * Provider IDs are useful link targets, but are not useful names. Apply the
 * same conservative checks to compact and space-normalized legacy labels.
 */
export function looksLikeOpaqueProjectSourceIdentifier(value) {
  const label = cleanLabel(value);
  if (!label) return false;
  const compact = label.replace(/\s+/g, "-");
  const words = label.split(" ");
  if (/^[0-9]+$/.test(label)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(label)) return true;
  if (/^[CGT][A-Z0-9]+$/.test(label)) return true;
  if (/^(?:doc|file)-[A-Za-z0-9_-]+$/i.test(compact)) return true;
  if (/(?:^|[-_ ])(?:id|uuid|token|key)(?:$|[-_ ])/i.test(label)) return true;
  if (
    words.length > 1
    && words.every((word) => /^[A-Z0-9_-]+$/.test(word))
    && words.some((word) => /\d/.test(word))
  ) return true;
  return /\d/.test(label) && /^[A-Za-z0-9_-]{6,}$/.test(label);
}

function readableLabel(value) {
  const label = cleanLabel(value);
  if (!label || looksLikeOpaqueProjectSourceIdentifier(label)) return null;
  return label;
}

function providerFallbackLabel(sourceType, parsed) {
  if (sourceType === PROJECT_SOURCE_TYPES.SLACK) return "Slack channel";
  if (sourceType === PROJECT_SOURCE_TYPES.GOOGLE) {
    return parsed?.hostname?.toLowerCase() === "drive.google.com"
      ? "Google Drive"
      : "Google Doc";
  }
  if (sourceType === PROJECT_SOURCE_TYPES.ATLASSIAN) return "Atlassian link";
  if (sourceType === PROJECT_SOURCE_TYPES.ASANA) return "Asana link";
  return "Other link";
}

function pathSegments(parsed) {
  return parsed.pathname.split("/").filter(Boolean).map(decodeSegment);
}

function rawPathCandidate(parsed) {
  return [...pathSegments(parsed)]
    .reverse()
    .find((segment) => segment && !IGNORED_PATH_SEGMENTS.has(segment.toLowerCase())) || null;
}

function formatPathLabel(candidate, parsed) {
  return cleanLabel((candidate || parsed.hostname)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()) || parsed.hostname;
}

/**
 * Derive a stable label without making an external request. This exported
 * compatibility helper returns the normalized form, while callers validate
 * the raw fragment before using it as visible text.
 */
export function deriveProjectSourceLabel(value) {
  const parsed = value instanceof URL ? value : parseProjectSourceUrl(value);
  if (!parsed) return null;
  return formatPathLabel(rawPathCandidate(parsed), parsed).slice(0, 160);
}

function isLegacyDerivedLabel(label, parsed) {
  const candidate = rawPathCandidate(parsed);
  if (!candidate) return false;
  return cleanLabel(label).toLowerCase() === formatPathLabel(candidate, parsed).toLowerCase();
}

function readablePathLabel(parsed) {
  const candidate = rawPathCandidate(parsed);
  const safeCandidate = readableLabel(candidate);
  return safeCandidate ? formatPathLabel(safeCandidate, parsed) : null;
}

function readableSlackUrlFragment(parsed) {
  const hash = readableLabel(parsed.hash.replace(/^#/, ""));
  if (hash) return hash.replace(/^#+/, "");
  return [...pathSegments(parsed)]
    .reverse()
    .filter((segment) => !IGNORED_PATH_SEGMENTS.has(segment.toLowerCase()))
    .map(readableLabel)
    .find(Boolean) || null;
}

export function getProjectSourceDisplayName({
  sourceType,
  rawUrl,
  label = null,
} = {}) {
  const parsed = rawUrl instanceof URL ? rawUrl : parseProjectSourceUrl(rawUrl);
  const type = sourceType || (parsed ? detectProjectSourceType(parsed) : null);
  const supplied = readableLabel(label);
  if (supplied && !(parsed && isLegacyDerivedLabel(supplied, parsed))) return supplied;
  if (type === PROJECT_SOURCE_TYPES.SLACK && parsed) {
    return readableSlackUrlFragment(parsed) || providerFallbackLabel(type, parsed);
  }
  if (
    (type === PROJECT_SOURCE_TYPES.GOOGLE
      || type === PROJECT_SOURCE_TYPES.ATLASSIAN
      || type === PROJECT_SOURCE_TYPES.ASANA
      || type === PROJECT_SOURCE_TYPES.OTHER)
    && parsed
  ) {
    return readablePathLabel(parsed) || providerFallbackLabel(type, parsed);
  }
  return providerFallbackLabel(type, parsed);
}